import toast from "react-hot-toast";

import { FolderIcon } from "lucide-react";
import uuid from "uuid";

import util from "utils/utils";

import { FileChooserWithDropzone } from "components/lib/Choosers";
import { UploadedFilesTable } from "components/lib/UploadedFilesTable";

import { FieldProps, IdeaFileValue } from "./FieldProps";

const MultiFile = ({
  field,
  fieldValue = [],
  updateIdea,
  isEditing,
  ideaId,
}: FieldProps) => {
  const files = (
    Array.isArray(fieldValue) ? fieldValue : []
  ) as IdeaFileValue[];

  const tableFiles = files.map((file) => ({
    id: file.value,
    name: file.name,
    url: file.value,
  }));

  const addFiles = (
    uploadedFiles: {
      savedName: string;
      fileName: string;
      signedDownloadRequest: string;
    }[],
  ) => {
    if (
      field.maxFiles &&
      files.length + uploadedFiles.length > field.maxFiles
    ) {
      toast.error("Your uploads exceed the maximum number of files allowed");
      return;
    }

    const updatedFiles = [
      ...uploadedFiles.map((file) => ({
        value: file.signedDownloadRequest,
        name: file.fileName,
        _id: uuid.v4(),
      })),
      ...files,
    ];
    updateIdea(updatedFiles, ["templated", field.id]);
  };

  const removeFile = (fileValue: string) => {
    util
      .confirm(
        "Are you sure you want to delete this file?",
        "This action cannot be undone, but you can always re-upload the file.",
      )
      .then(() => {
        const updatedFiles = files.filter((file) => file.value !== fileValue);
        updateIdea(updatedFiles, ["templated", field.id]);
      })
      .catch(() => {});
  };

  const hasMaxFiles = !!(field.maxFiles && files.length >= field.maxFiles);

  if (!isEditing) {
    return <UploadedFilesTable files={tableFiles} />;
  }

  return (
    <div>
      <FileChooserWithDropzone
        forType="idea"
        forId={ideaId}
        allowMultiple={true}
        onCompleteMultiple={addFiles}
        onError={toast.error}
        disabled={hasMaxFiles}
        popupProps={{ position: "bottom center", disabled: hasMaxFiles }}
        dropzoneProps={{
          title: "Upload multiple files",
          subtitle: field.maxFiles
            ? `Maximum of ${util.pluralise(field.maxFiles, "file", "files")}`
            : undefined,
          defaultIcon: FolderIcon,
          disabled: hasMaxFiles,
        }}
      />
      <UploadedFilesTable files={tableFiles} onRemove={removeFile} />
    </div>
  );
};

export default MultiFile;
